import { cn, focusRing } from "@rhema/ui";
import type { ReactNode } from "react";
import type { SignInProvider } from "./SignInScreen";

// The marks as each company publishes them for sign-in buttons.
function GoogleLogo() {
  return (
    <svg width="18" height="18" viewBox="0 0 48 48" aria-hidden="true">
      <path
        fill="#FFC107"
        d="M43.6 20.1H42V20H24v8h11.3C33.7 32.7 29.2 36 24 36c-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.8 1.2 8 3l5.7-5.7C34 6.1 29.3 4 24 4 13 4 4 13 4 24s9 20 20 20 20-9 20-20c0-1.3-.1-2.6-.4-3.9z"
      />
      <path
        fill="#FF3D00"
        d="m6.3 14.7 6.6 4.8C14.7 15.1 19 12 24 12c3.1 0 5.8 1.2 8 3l5.7-5.7C34 6.1 29.3 4 24 4 16.3 4 9.7 8.3 6.3 14.7z"
      />
      <path
        fill="#4CAF50"
        d="M24 44c5.2 0 9.9-2 13.4-5.2l-6.2-5.2c-2 1.5-4.5 2.4-7.2 2.4-5.2 0-9.6-3.3-11.3-7.9l-6.5 5C9.5 39.6 16.2 44 24 44z"
      />
      <path
        fill="#1976D2"
        d="M43.6 20.1H42V20H24v8h11.3c-.8 2.2-2.2 4.2-4.1 5.6l6.2 5.2C37 39.2 44 34 44 24c0-1.3-.1-2.6-.4-3.9z"
      />
    </svg>
  );
}

function AppleLogo() {
  return (
    <svg width="17" height="17" viewBox="0 0 24 24" aria-hidden="true" className="text-ink">
      <path
        fill="currentColor"
        d="M12.15 6.9c-.95 0-2.42-1.08-3.96-1.04-2.04.03-3.91 1.18-4.96 3.01-2.12 3.68-.55 9.1 1.52 12.09 1.01 1.45 2.21 3.09 3.79 3.04 1.52-.07 2.09-.99 3.94-.99 1.83 0 2.35.99 3.96.95 1.64-.03 2.68-1.48 3.68-2.95 1.16-1.69 1.64-3.33 1.66-3.42-.04-.01-3.18-1.22-3.22-4.86-.03-3.04 2.48-4.49 2.6-4.56-1.43-2.09-3.62-2.32-4.39-2.38-2-.16-3.68 1.09-4.61 1.09zm3.38-3.07c.84-1.01 1.4-2.43 1.25-3.83-1.21.05-2.66.81-3.53 1.82-.78.9-1.45 2.34-1.27 3.71 1.34.1 2.71-.69 3.55-1.7"
      />
    </svg>
  );
}

function MicrosoftLogo() {
  return (
    <svg width="16" height="16" viewBox="0 0 23 23" aria-hidden="true">
      <path fill="#F25022" d="M0 0h11v11H0z" />
      <path fill="#7FBA00" d="M12 0h11v11H12z" />
      <path fill="#00A4EF" d="M0 12h11v11H0z" />
      <path fill="#FFB900" d="M12 12h11v11H12z" />
    </svg>
  );
}

const PROVIDERS: { id: SignInProvider; name: string; logo: () => ReactNode }[] = [
  { id: "google", name: "Google", logo: GoogleLogo },
  { id: "apple", name: "Apple", logo: AppleLogo },
  { id: "microsoft", name: "Microsoft", logo: MicrosoftLogo },
];

/** The logo of a sign-in provider, at the size its button uses. */
export function ProviderLogo({ provider }: { provider: SignInProvider }) {
  const Logo = PROVIDERS.find((p) => p.id === provider)?.logo;
  return Logo ? <Logo /> : null;
}

/** One quiet outlined button, the shape every other way to sign in shares. */
export const optionClass = cn(
  "flex h-[50px] min-w-0 items-center justify-center gap-1.5 rounded-field border border-line bg-surface px-1.5",
  "text-sm font-medium text-ink transition-colors hover:bg-linen disabled:cursor-not-allowed disabled:opacity-50",
  // Phones are narrow enough that "Microsoft" needs the smaller size to fit beside its logo.
  "min-[420px]:gap-2 min-[420px]:px-2 min-[420px]:text-[15px]",
  focusRing,
);

/** Google, Apple and Microsoft, each with its logo, side by side. */
export function ProviderButtons({
  verb,
  busy,
  onProvider,
}: {
  /** How each button's accessible name starts, such as "Continue with". */
  verb: string;
  busy: boolean;
  onProvider: (provider: SignInProvider) => void;
}) {
  return (
    <div className="grid grid-cols-3 gap-2">
      {PROVIDERS.map(({ id, name, logo: Logo }) => (
        <button
          key={id}
          type="button"
          className={optionClass}
          aria-label={`${verb} ${name}`}
          disabled={busy}
          onClick={() => onProvider(id)}
        >
          <Logo />
          <span className="truncate">{name}</span>
        </button>
      ))}
    </div>
  );
}

export function OrDivider() {
  return (
    <div className="my-5 flex items-center gap-3.5 text-[13px] text-ink-meta [@media(max-height:700px)]:my-4">
      <span className="h-px flex-1 bg-line" />
      or
      <span className="h-px flex-1 bg-line" />
    </div>
  );
}
